import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FdModal from '../components/FdModal';
import { adminApi, resolveAdminApiError } from '../services/adminApi';
import useScopePermissions from '../hooks/useScopePermissions';
import './AdminPage.css';

const DEFAULT_ROLE_TEMPLATES = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access across all modules and branches.',
    access_labels: ['Dashboard', 'Product Setup', 'Agents', 'Branch Management', 'Sales', 'Collections', 'Commissions', 'Request for Payment', 'Liquidation', 'Replenishment', 'Reports', 'Admin'],
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Dashboard, collections, commissions, request for payment, and reports.',
    access_labels: ['Dashboard', 'Collections', 'Commissions', 'Request for Payment', 'Reports'],
  },
  {
    value: 'agent',
    label: 'Agent',
    description: 'Dashboard and collections access.',
    access_labels: ['Dashboard', 'Collections'],
  },
  {
    value: 'accounting',
    label: 'Accounting',
    description: 'Dashboard, liquidation, replenishment, and reports.',
    access_labels: ['Dashboard', 'Liquidation', 'Replenishment', 'Reports'],
  },
  {
    value: 'user',
    label: 'User',
    description: 'Basic dashboard access.',
    access_labels: ['Dashboard'],
  },
];

function formatDate(value) {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function roleBadgeClass(role) {
  if (role === 'admin') {
    return 'status-active';
  }
  if (role === 'manager') {
    return 'status-pending';
  }
  if (role === 'accounting') {
    return 'status-inactive';
  }
  return 'status-inactive';
}

function statusBadgeClass(status) {
  if (status === 'active') {
    return 'status-active';
  }
  if (status === 'inactive') {
    return 'status-inactive';
  }
  return 'status-cancelled';
}

function createInviteForm() {
  return {
    username: '',
    email: '',
    full_name: '',
    password: '',
    passwordConfirmation: '',
    role: 'user',
    branch_id: '',
    branch_name: '',
    scope_ids: [],
  };
}

function normalizeScopeIds(scopeIds) {
  if (!Array.isArray(scopeIds)) {
    return [];
  }

  return Array.from(
    new Set(
      scopeIds
        .map((scopeId) => String(scopeId || '').trim())
        .filter((scopeId) => scopeId.length > 0)
    )
  );
}

export default function AdminPage() {
  const { hasScope } = useScopePermissions();
  const canInviteUser = hasScope('oauth-users.create');
  const canEditUserAccess = hasScope('oauth-users.update');
  const canEditScopeAssignments = hasScope('oauth-users.permissions.update');
  const canResetPassword = hasScope('oauth-users.update');

  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState(DEFAULT_ROLE_TEMPLATES);
  const [permissionModules, setPermissionModules] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(createInviteForm);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: 'user',
    status: 'active',
    branch_id: '',
    branch_name: '',
    scope_ids: [],
    new_password: '',
  });

  const roleMap = useMemo(() => {
    const map = {};
    roles.forEach((role) => {
      map[role.value] = role;
    });
    return map;
  }, [roles]);

  const permissionModulesWithActions = useMemo(() => {
    const modules = Array.isArray(permissionModules) ? permissionModules : [];
    return modules
      .map((module) => ({
        ...module,
        actions: Array.isArray(module?.actions)
          ? module.actions.filter((action) => action?.is_available !== false && String(action.scope_id || '').trim() !== '')
          : [],
      }))
      .filter((module) => module.actions.length > 0);
  }, [permissionModules]);

  const allAvailableScopeIds = useMemo(() => (
    permissionModulesWithActions.flatMap((module) => module.actions.map((action) => action.scope_id))
  ), [permissionModulesWithActions]);

  function defaultRoleScopeIds(roleValue) {
    return normalizeScopeIds(roleMap[roleValue]?.scope_ids || []);
  }

  function toggleInviteScope(scopeId) {
    const normalizedScopeId = String(scopeId || '').trim();
    if (!normalizedScopeId) {
      return;
    }

    setInviteForm((previous) => {
      const current = normalizeScopeIds(previous.scope_ids);
      const exists = current.includes(normalizedScopeId);
      return {
        ...previous,
        scope_ids: exists
          ? current.filter((item) => item !== normalizedScopeId)
          : [...current, normalizedScopeId],
      };
    });
  }

  function toggleEditScope(scopeId) {
    const normalizedScopeId = String(scopeId || '').trim();
    if (!normalizedScopeId) {
      return;
    }

    setEditForm((previous) => {
      const current = normalizeScopeIds(previous.scope_ids);
      const exists = current.includes(normalizedScopeId);
      return {
        ...previous,
        scope_ids: exists
          ? current.filter((item) => item !== normalizedScopeId)
          : [...current, normalizedScopeId],
      };
    });
  }

  const loadData = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const response = await adminApi.listUsers({ bustCache: true });
      setUsers(response?.data?.users || []);
      setBranches(response?.data?.branches || []);
      setRoles(response?.data?.roles || DEFAULT_ROLE_TEMPLATES);
      setPermissionModules(response?.data?.permission_modules || []);
    } catch (err) {
      setUsers([]);
      setBranches([]);
      setRoles(DEFAULT_ROLE_TEMPLATES);
      setPermissionModules([]);
      setError(resolveAdminApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredUsers = useMemo(() => {
    const key = String(search || '').trim().toLowerCase();
    if (!key) {
      return users;
    }

    return users.filter((user) => {
      const text = [
        user.full_name,
        user.username,
        user.email,
        user.role,
        user.branch,
        user.status,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');

      return text.includes(key);
    });
  }, [users, search]);

  const selectedInviteScopes = useMemo(
    () => normalizeScopeIds(inviteForm.scope_ids),
    [inviteForm.scope_ids]
  );

  const selectedEditScopes = useMemo(
    () => normalizeScopeIds(editForm.scope_ids),
    [editForm.scope_ids]
  );

  function resetInviteForm() {
    setInviteForm({
      ...createInviteForm(),
      scope_ids: defaultRoleScopeIds('user'),
    });
  }

  function openInviteModal() {
    if (!canInviteUser) {
      setError('You do not have permission to create users.');
      return;
    }

    resetInviteForm();
    setIsInviteOpen(true);
  }

  function openEditModal(user) {
    if (!canEditUserAccess) {
      setError('You do not have permission to update user access.');
      return;
    }

    setSelectedUser(user);
    setEditForm({
      role: user?.role || 'user',
      status: user?.status || 'active',
      branch_id: user?.branch_id ? String(user.branch_id) : '',
      branch_name: user?.branch || '',
      scope_ids: normalizeScopeIds(user?.scope_ids || defaultRoleScopeIds(user?.role || 'user')),
      new_password: '',
    });
    setIsEditOpen(true);
  }

  function openResetPasswordModal(user) {
    if (!canResetPassword) {
      setError('You do not have permission to reset user passwords.');
      return;
    }

    setResetPasswordTarget(user);
    setResetPasswordResult(null);
    setIsResetPasswordOpen(true);
  }

  async function submitResetPassword() {
    if (!canResetPassword || !resetPasswordTarget) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await adminApi.resetUserPassword(resetPasswordTarget.id);
      setResetPasswordResult(response?.data?.generated_credentials || null);
    } catch (err) {
      setError(resolveAdminApiError(err));
      setIsResetPasswordOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitInvite() {
    if (!canInviteUser) {
      setError('You do not have permission to create users.');
      return;
    }

    const username = String(inviteForm.username || '').trim();
    const email = String(inviteForm.email || '').trim();
    const fullName = String(inviteForm.full_name || '').trim();
    const password = String(inviteForm.password || '');
    const passwordConfirmation = String(inviteForm.passwordConfirmation || '');
    const role = String(inviteForm.role || 'user').trim();
    const branchId = inviteForm.branch_id ? Number(inviteForm.branch_id) : null;
    const branchName = String(inviteForm.branch_name || '').trim();
    const scopeIds = normalizeScopeIds(inviteForm.scope_ids);

    if (!username || !email || !password || !passwordConfirmation) {
      return;
    }

    if (!/^[A-Za-z0-9._-]+$/.test(username)) {
      setError('Username may only contain letters, numbers, dots, underscores, and hyphens.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password.length > 100) {
      setError('Password must not exceed 100 characters.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Password confirmation does not match.');
      return;
    }

    if (role !== 'admin' && !branchId && !branchName) {
      setError('Branch is required for non-admin users.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await adminApi.inviteUser({
        username,
        email,
        full_name: fullName || null,
        password,
        password_confirmation: passwordConfirmation,
        role,
        branch_id: branchId,
        branch_name: branchId ? null : (branchName || null),
        status: 'active',
        scope_ids: scopeIds,
      });

      setMessage(response?.message || 'User created successfully.');
      await loadData({ clearFeedback: false });
      setIsInviteOpen(false);
      resetInviteForm();
    } catch (err) {
      setError(resolveAdminApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitAccessUpdate() {
    if (!canEditUserAccess) {
      setError('You do not have permission to update user access.');
      return;
    }

    if (!selectedUser) {
      return;
    }

    const role = String(editForm.role || 'user').trim();
    const status = String(editForm.status || 'active').trim();
    const branchId = editForm.branch_id ? Number(editForm.branch_id) : null;
    const branchName = String(editForm.branch_name || '').trim();
    const scopeIds = normalizeScopeIds(editForm.scope_ids);
    const newPassword = String(editForm.new_password || '').trim();

    if (role !== 'admin' && !branchId && !branchName) {
      setError('Branch is required for non-admin users.');
      return;
    }

    if (newPassword && newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      await adminApi.updateUserAccess(selectedUser.id, {
        role,
        status,
        branch_id: branchId,
        branch_name: branchId ? null : (branchName || null),
        scope_ids: scopeIds,
        ...(newPassword ? { new_password: newPassword } : {}),
      });

      setMessage(newPassword
        ? 'User access updated and password changed. All active sessions for this user have been revoked.'
        : 'User access updated successfully.');
      await loadData({ clearFeedback: false });
      setIsEditOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(resolveAdminApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Oauth Users</h1>
        <p>Manage oauth users, branch assignments, and per-module action permissions.</p>
      </section>

      <section className="dashboard-content admin-page-content">
        <div className="admin-toolbar">
          <input
            type="text"
            placeholder="Search name, username, email, role, branch..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {canInviteUser ? (
            <button type="button" className="profile-secondary-btn" onClick={openInviteModal}>
              + Create User
            </button>
          ) : null}
        </div>

        {error ? <div className="error-box">{error}</div> : null}
        {message ? <div className="success-box">{message}</div> : null}

        <div className="admin-role-grid">
          {roles.map((role) => (
            <div key={role.value} className="admin-role-card">
              <div className="admin-role-card-top">
                <span className={`status-badge ${roleBadgeClass(role.value)}`}>{role.label}</span>
              </div>
              <p>{role.description}</p>
              <div className="admin-role-tags">
                {(role.access_labels || []).map((label) => (
                  <span key={`${role.value}-${label}`}>{label}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {isLoading ? (
          <div className="dashboard-loading">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="dashboard-empty">No users found.</div>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Branch</th>
                  <th>Module Access</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const roleTemplate = roleMap[user.role] || null;
                  const accessLabels = user.access_labels || roleTemplate?.access_labels || [];

                  return (
                    <tr key={user.id}>
                      <td>
                        <p className="admin-user-name">{user.full_name || '-'}</p>
                        <span className="admin-subline">{user.username || '-'}</span>
                      </td>
                      <td>{user.email || '-'}</td>
                      <td>
                        <span className={`status-badge ${roleBadgeClass(user.role)}`}>
                          {roleTemplate?.label || user.role || 'User'}
                        </span>
                      </td>
                      <td>{user.role === 'admin' ? 'All Branches' : (user.branch || '-')}</td>
                      <td>
                        <div className="admin-access-tags">
                          {accessLabels.length === 0
                            ? <span>Dashboard</span>
                            : accessLabels.map((label) => <span key={`${user.id}-${label}`}>{label}</span>)}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${statusBadgeClass(user.status)}`}>
                          {user.status || 'inactive'}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="admin-actions-cell">
                          {canEditUserAccess ? (
                            <button
                              type="button"
                              className="profile-secondary-btn profile-secondary-btn-xs"
                              onClick={() => openEditModal(user)}
                            >
                              Edit
                            </button>
                          ) : null}
                          {canResetPassword ? (
                            <button
                              type="button"
                              className="profile-secondary-btn profile-secondary-btn-xs admin-btn-danger"
                              onClick={() => openResetPasswordModal(user)}
                            >
                              Reset Password
                            </button>
                          ) : null}
                          {!canEditUserAccess && !canResetPassword ? <span>-</span> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <FdModal
        isOpen={isInviteOpen}
        onClose={() => {
          setIsInviteOpen(false);
          resetInviteForm();
        }}
        eyebrow="Oauth Users"
        title="Create New User"
        subtitle="Create a user account and assign role plus action permissions."
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={() => {
                setIsInviteOpen(false);
                resetInviteForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitInvite}
              disabled={
                isSubmitting
                || !canInviteUser
                || !String(inviteForm.username || '').trim()
                || !String(inviteForm.email || '').trim()
                || !String(inviteForm.password || '')
                || !String(inviteForm.passwordConfirmation || '')
              }
            >
              {isSubmitting ? 'Saving...' : 'Create'}
            </button>
          </>
        )}
      >
        <div className="admin-modal-grid">
          <div className="fd-field fd-field-wide">
            <label>Username</label>
            <input
              type="text"
              value={inviteForm.username}
              onChange={(event) => setInviteForm((previous) => ({ ...previous, username: event.target.value }))}
              placeholder="username"
              autoComplete="username"
              maxLength={80}
            />
          </div>
          <div className="fd-field fd-field-wide">
            <label>Email Address</label>
            <input
              type="email"
              value={inviteForm.email}
              onChange={(event) => setInviteForm((previous) => ({ ...previous, email: event.target.value }))}
              placeholder="user@example.com"
              maxLength={191}
            />
          </div>
          <div className="fd-field fd-field-wide">
            <label>Full Name</label>
            <input
              type="text"
              value={inviteForm.full_name}
              onChange={(event) => setInviteForm((previous) => ({ ...previous, full_name: event.target.value }))}
              placeholder="Optional"
              maxLength={150}
            />
          </div>
          <div className="fd-field">
            <label>Password</label>
            <input
              type="password"
              value={inviteForm.password}
              onChange={(event) => setInviteForm((previous) => ({ ...previous, password: event.target.value }))}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              maxLength={100}
            />
          </div>
          <div className="fd-field">
            <label>Confirm Password</label>
            <input
              type="password"
              value={inviteForm.passwordConfirmation}
              onChange={(event) => setInviteForm((previous) => ({ ...previous, passwordConfirmation: event.target.value }))}
              placeholder="Confirm password"
              autoComplete="new-password"
              maxLength={100}
            />
          </div>
          <div className="fd-field">
            <label>Role</label>
            <select
              value={inviteForm.role}
              onChange={(event) => {
                const nextRole = event.target.value;
                setInviteForm((previous) => ({
                  ...previous,
                  role: nextRole,
                  scope_ids: defaultRoleScopeIds(nextRole),
                }));
              }}
            >
              {roles.map((role) => (
                <option key={`invite-role-${role.value}`} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <div className="fd-field">
            <label>Status</label>
            <select value="active" disabled>
              <option value="active">active</option>
            </select>
          </div>
          {inviteForm.role !== 'admin' ? (
            <>
              <div className="fd-field">
                <label>Branch (Select)</label>
                <select
                  value={inviteForm.branch_id}
                  onChange={(event) => setInviteForm((previous) => ({ ...previous, branch_id: event.target.value }))}
                >
                  <option value="">Select branch</option>
                  {branches.map((branch) => (
                    <option key={`invite-branch-${branch.id}`} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fd-field">
                <label>Branch (Manual)</label>
                <input
                  type="text"
                  value={inviteForm.branch_name}
                  onChange={(event) => setInviteForm((previous) => ({ ...previous, branch_name: event.target.value }))}
                  placeholder="Optional if branch is selected"
                  disabled={Boolean(inviteForm.branch_id)}
                />
              </div>
            </>
          ) : (
            <div className="admin-info-box">
              Admin users automatically get all branches and all modules.
            </div>
          )}

          <div className="admin-scope-section">
            <div className="admin-scope-header">
              <p>Module Action Permissions</p>
              <div className="admin-scope-header-actions">
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setInviteForm((previous) => ({ ...previous, scope_ids: defaultRoleScopeIds(previous.role) }))}
                  disabled={!canEditScopeAssignments}
                >
                  Use Role Defaults
                </button>
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setInviteForm((previous) => ({ ...previous, scope_ids: allAvailableScopeIds }))}
                  disabled={!canEditScopeAssignments}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setInviteForm((previous) => ({ ...previous, scope_ids: [] }))}
                  disabled={!canEditScopeAssignments}
                >
                  Clear
                </button>
              </div>
            </div>

            {permissionModulesWithActions.length === 0 ? (
              <div className="admin-info-box">No available scope modules returned by API.</div>
            ) : (
              <div className="admin-scope-grid">
                {permissionModulesWithActions.map((module) => (
                  <div key={`invite-scope-${module.key}`} className="admin-scope-card">
                    <h4>{module.label}</h4>
                    {module.description ? <p>{module.description}</p> : null}
                    <div className="admin-scope-action-list">
                      {module.actions.map((action) => (
                        <label key={`invite-scope-${module.key}-${action.scope_id}`} className="admin-scope-action-item">
                          <input
                            type="checkbox"
                            checked={selectedInviteScopes.includes(action.scope_id)}
                            onChange={() => toggleInviteScope(action.scope_id)}
                            disabled={!canEditScopeAssignments}
                          />
                          <span>{action.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedUser(null);
          setEditForm((previous) => ({ ...previous, new_password: '' }));
        }}
        eyebrow="Oauth Users"
        title="Edit User Access"
        subtitle="Update role, status, branch assignment, and action permissions."
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={() => {
                setIsEditOpen(false);
                setSelectedUser(null);
                setEditForm((previous) => ({ ...previous, new_password: '' }));
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitAccessUpdate}
              disabled={isSubmitting || !canEditUserAccess || !selectedUser}
            >
              {isSubmitting ? 'Saving...' : 'Update Access'}
            </button>
          </>
        )}
      >
        <div className="admin-modal-grid">
          {selectedUser ? (
            <div className="admin-edit-preview">
              <p>{selectedUser.full_name || selectedUser.username}</p>
              <span className="admin-edit-preview-username">@{selectedUser.username}</span>
              <span>{selectedUser.email}</span>
            </div>
          ) : null}

          <div className="fd-field">
            <label>Role</label>
            <select
              value={editForm.role}
              onChange={(event) => {
                const nextRole = event.target.value;
                setEditForm((previous) => ({
                  ...previous,
                  role: nextRole,
                  scope_ids: defaultRoleScopeIds(nextRole),
                }));
              }}
            >
              {roles.map((role) => (
                <option key={`edit-role-${role.value}`} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <div className="fd-field">
            <label>Status</label>
            <select
              value={editForm.status}
              onChange={(event) => setEditForm((previous) => ({ ...previous, status: event.target.value }))}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
              <option value="locked">locked</option>
            </select>
          </div>

          {editForm.role !== 'admin' ? (
            <>
              <div className="fd-field">
                <label>Branch (Select)</label>
                <select
                  value={editForm.branch_id}
                  onChange={(event) => setEditForm((previous) => ({ ...previous, branch_id: event.target.value }))}
                >
                  <option value="">Select branch</option>
                  {branches.map((branch) => (
                    <option key={`edit-branch-${branch.id}`} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fd-field">
                <label>Branch (Manual)</label>
                <input
                  type="text"
                  value={editForm.branch_name}
                  onChange={(event) => setEditForm((previous) => ({ ...previous, branch_name: event.target.value }))}
                  placeholder="Optional if branch is selected"
                  disabled={Boolean(editForm.branch_id)}
                />
              </div>
            </>
          ) : (
            <div className="admin-info-box">
              Admin users automatically get all branches and all modules.
            </div>
          )}

          <div className="admin-password-section">
            <div className="admin-scope-header">
              <p>Change Password</p>
              <button
                type="button"
                className="profile-secondary-btn profile-secondary-btn-xs"
                onClick={() => {
                  const generated = `Fd${Math.random().toString(36).slice(2, 4).toUpperCase()}!${String(Math.floor(1000 + Math.random() * 9000))}${Math.random().toString(36).slice(2, 4).toLowerCase()}`;
                  setEditForm((previous) => ({ ...previous, new_password: generated }));
                }}
              >
                Generate Password
              </button>
            </div>
            <div className="fd-field fd-field-wide">
              <label>New Password <span className="admin-field-optional">(leave blank to keep current)</span></label>
              <input
                type="text"
                value={editForm.new_password}
                onChange={(event) => setEditForm((previous) => ({ ...previous, new_password: event.target.value }))}
                placeholder="Min. 8 characters"
              />
            </div>
          </div>

          <div className="admin-scope-section">
            <div className="admin-scope-header">
              <p>Module Action Permissions</p>
              <div className="admin-scope-header-actions">
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setEditForm((previous) => ({ ...previous, scope_ids: defaultRoleScopeIds(previous.role) }))}
                  disabled={!canEditScopeAssignments}
                >
                  Use Role Defaults
                </button>
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setEditForm((previous) => ({ ...previous, scope_ids: allAvailableScopeIds }))}
                  disabled={!canEditScopeAssignments}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="profile-secondary-btn profile-secondary-btn-xs"
                  onClick={() => setEditForm((previous) => ({ ...previous, scope_ids: [] }))}
                  disabled={!canEditScopeAssignments}
                >
                  Clear
                </button>
              </div>
            </div>

            {permissionModulesWithActions.length === 0 ? (
              <div className="admin-info-box">No available scope modules returned by API.</div>
            ) : (
              <div className="admin-scope-grid">
                {permissionModulesWithActions.map((module) => (
                  <div key={`edit-scope-${module.key}`} className="admin-scope-card">
                    <h4>{module.label}</h4>
                    {module.description ? <p>{module.description}</p> : null}
                    <div className="admin-scope-action-list">
                      {module.actions.map((action) => (
                        <label key={`edit-scope-${module.key}-${action.scope_id}`} className="admin-scope-action-item">
                          <input
                            type="checkbox"
                            checked={selectedEditScopes.includes(action.scope_id)}
                            onChange={() => toggleEditScope(action.scope_id)}
                            disabled={!canEditScopeAssignments}
                          />
                          <span>{action.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </FdModal>
      <FdModal
        isOpen={isResetPasswordOpen}
        onClose={() => {
          setIsResetPasswordOpen(false);
          setResetPasswordTarget(null);
          setResetPasswordResult(null);
        }}
        eyebrow="Oauth Users"
        title="Reset Password"
        subtitle={resetPasswordResult
          ? 'Password reset successfully. Share the credentials below with the user.'
          : `Generate a new temporary password for ${resetPasswordTarget?.full_name || resetPasswordTarget?.username || 'this user'}. All active sessions will be revoked.`}
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={() => {
                setIsResetPasswordOpen(false);
                setResetPasswordTarget(null);
                setResetPasswordResult(null);
              }}
              disabled={isSubmitting}
            >
              {resetPasswordResult ? 'Close' : 'Cancel'}
            </button>
            {!resetPasswordResult ? (
              <button
                type="button"
                className="fd-btn fd-btn-danger"
                onClick={submitResetPassword}
                disabled={isSubmitting || !resetPasswordTarget}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            ) : null}
          </>
        )}
      >
        <div className="admin-modal-grid">
          {resetPasswordTarget && !resetPasswordResult ? (
            <div className="admin-edit-preview">
              <p>{resetPasswordTarget.full_name || resetPasswordTarget.username}</p>
              <span className="admin-edit-preview-username">@{resetPasswordTarget.username}</span>
              <span>{resetPasswordTarget.email}</span>
            </div>
          ) : null}
          {resetPasswordResult ? (
            <div className="admin-credentials-box">
              <p>Username: <strong>{resetPasswordResult.username}</strong></p>
              <p>New Temporary Password: <strong>{resetPasswordResult.temporary_password}</strong></p>
            </div>
          ) : null}
        </div>
      </FdModal>
    </div>
  );
}
